import { createSelector } from 'reselect';
import { selectPermissions } from 'selectors/navigation/util';
import { hasPermission } from 'utils/permissions';
import Permission from 'xm-constants/permissions';

const createSelectorForPermission = permission =>
  createSelector([selectPermissions], userPermissions =>
    hasPermission(userPermissions, permission),
  );

export const hasCreateWorkflowPermission = createSelectorForPermission(
  Permission.ABILITY_ACT_CREATE_APPLICATION,
);

export const hasImportWorkflowPermission = createSelectorForPermission(
  Permission.ABILITY_ACT_IMPORT_APPLICATION,
);

export const hasEditWorkflowPermission = createSelectorForPermission(
  Permission.ABILITY_ACT_EDIT_APPLICATION,
);

export const hasViewWorkflowPermission = createSelectorForPermission(
  Permission.ABILITY_ACT_VIEW_APPLICATIONS,
);

export const hasDeleteWorkflowPermission = createSelectorForPermission(
  Permission.ABILITY_ACT_DELETE_APPLICATION,
);

export const hasEditAllWorkflowsPermission = createSelectorForPermission(
  Permission.ABILITY_ACT_EDIT_ALL_WORKFLOWS,
);

export const hasViewGroupDetailsPermission = createSelectorForPermission(
  Permission.VIEW_SCREEN_GROUP_DETAILS,
);

export const hasEditGroupDetailsPermission = createSelectorForPermission(
  Permission.ABILITY_ACT_EDIT_ANY_GROUP,
);

export const hasAddGroupPermission = createSelectorForPermission(
  Permission.VIEW_SCREEN_ADD_GROUP,
);

export const hasGroupSupervisionPermission = createSelectorForPermission(
  Permission.ABILITY_ACT_GROUPSUPERVISOR,
);

export const hasViewDynamicTeams = createSelectorForPermission(
  Permission.VIEW_SCREEN_DYNAMICTEAMS,
);

export const hasViewSites = createSelectorForPermission(
  Permission.VIEW_SCREEN_SITES,
);

export const hasViewDynamicTeamDetails = createSelectorForPermission(
  Permission.VIEW_SCREEN_DYNAMICTEAMDETAILS,
);

export const hasViewSiteDetails = createSelectorForPermission(
  Permission.VIEW_SCREEN_SITEDETAILS,
);

export const hasManageDeviceNames = createSelectorForPermission(
  Permission.ABILITY_ACT_MANAGE_DEVICE_NAMES,
);

export const hasViewGroupPerformanceReport = createSelectorForPermission(
  Permission.VIEW_SCREEN_GROUPPERFORMANCEREPORT,
);

export const hasViewIncidents = createSelectorForPermission(
  Permission.ABILITY_ACT_VIEW_INCIDENTS,
);

export const hasEditIncidents = createSelectorForPermission(
  Permission.ABILITY_ACT_EDIT_INCIDENTS,
);

export const hasViewIncidentSeverity = createSelectorForPermission(
  Permission.ABILITY_ACT_VIEW_INCIDENT_SEVERITY,
);

export const hasViewAdminMenu = createSelectorForPermission(
  Permission.VIEW_MENU_ADMIN,
);

export const hasViewMenuDeveloper = createSelectorForPermission(
  Permission.VIEW_MENU_DEVELOPER,
);

export const hasManageStakeholderUsers = createSelectorForPermission(
  Permission.ABILITY_ACT_MANAGE_STAKEHOLDER_USERS,
);

export const hasViewScreenOrgPlanOverview = createSelectorForPermission(
  Permission.VIEW_SCREEN_ORG_PLAN_OVERVIEW,
);

export const hasEventAdministration = createSelectorForPermission(
  Permission.ABILITY_ACT_EVENT_ADMINISTRATION,
);

export const hasViewUserListing = createSelectorForPermission(
  Permission.VIEW_SCREEN_USERS,
);

export const hasViewUserPerformanceReport = createSelectorForPermission(
  Permission.VIEW_SCREEN_USER_PERFORMANCE_REPORT,
);

export const hasManagePasswordPolicy = createSelectorForPermission(
  Permission.ABILITY_ACT_MANAGE_PASSWORD_POLICY,
);

export const hasViewMenuItemService = createSelectorForPermission(
  Permission.VIEW_MENUITEM_SERVICES,
);

export const hasManageUserProperties = createSelectorForPermission(
  Permission.ABILITY_ACT_MANAGE_USER_PROPERTIES,
);

export const hasOverrideExternalDataLock = createSelectorForPermission(
  Permission.ABILITY_OVERRIDE_EXTERNALDATALOCK,
);

export const hasViewTeamDetails = createSelectorForPermission(
  Permission.VIEW_SCREEN_TEAMDETAILS,
);

export const hasViewScreenGroups = createSelectorForPermission(
  Permission.VIEW_SCREEN_GROUPS,
);

export const hasCreateService = createSelectorForPermission(
  Permission.ABILITY_ACT_CREATE_SERVICE,
);

export const hasChangePassword = createSelectorForPermission(
  Permission.VIEW_SCREEN_CHANGEPASSWORD,
);

export const hasViewScreenExternalData = createSelectorForPermission(
  Permission.VIEW_SCREEN_EXTERNALDATA,
);

export const hasViewConferenceBridges = createSelectorForPermission(
  Permission.ABILITY_ACT_VIEW_CONFERENCE_BRIDGES,
);

export const hasViewNotificationReport = createSelectorForPermission(
  Permission.ABILITY_ACT_NOTIFICATIONREPORT,
);

export const hasManageIncidentTypes = createSelectorForPermission(
  Permission.ABILITY_ACT_MANAGE_INCIDENT_TYPES,
);

export const hasManageFloodControl = createSelectorForPermission(
  Permission.ABILITY_ACT_MANAGEFLOODCONTROL,
);

// TODO: consolidate with other VIEW_MENU_REPORTING permission when COR-73576 is in:
export const hasViewReporting = createSelectorForPermission(
  Permission.VIEW_MENU_REPORTING,
);

export const hasViewAlertsList = createSelectorForPermission(
  Permission.VIEW_MENUITEM_EVENT_ACTIVITY_REPORT,
);

export const hasViewMessagingList = createSelectorForPermission(
  Permission.VIEW_MENU_MESSAGING,
);

export const hasViewMenuReporting = createSelectorForPermission(
  Permission.VIEW_MENU_REPORTING,
);

export const hasViewDictionary = createSelectorForPermission(
  Permission.ABILITY_ACT_MANAGE_TTS_DICTIONARY,
);

export const hasViewRecording = createSelectorForPermission(
  Permission.VIEW_MENUITEM_PHONE_RECORDINGS,
);

export const hasViewWhosOnDuty = createSelectorForPermission(
  Permission.VIEW_SCREEN_WHOS_ON_DUTY,
);

export const hasViewHistoricalOnCall = createSelectorForPermission(
  Permission.VIEW_SCREEN_HISTORICAL_ONCALL,
);

export const hasViewSystemAuditReport = createSelectorForPermission(
  Permission.VIEW_SCREEN_SYSTEMAUDITREPORT,
);

export const hasManageHolidays = createSelectorForPermission(
  Permission.ABILITY_ACT_MANAGE_HOLIDAYS,
);

export const hasEditLiveCallRouting = createSelectorForPermission(
  Permission.ABILITY_ACT_EDITLIVECALLROUTING,
);
